import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

import {
  useGetRoleTagsQuery,
  useGetRolesQuery,
  useGetUserRolesQuery,
  useReorderRoleTagsMutation,
  RoleListItem,
  RoleTag,
} from "../../app/services/roles";
import { roleStatusIcon, roleStatusLabel } from "./roleAcceptPosition";
import { pickRoleDisplayName } from "./roleDisplay";
// @ts-ignore - authuser.js は型定義なし
import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
import FilterChipBar from "../filter-chip/FilterChipBar";
import RadioCard from "../form-item/RadioCard";
import RadioCardGroup from "../form-item/RadioCardGroup";
import StatusBadge from "../status-badge/StatusBadge";
import type { ShotTag } from "../../app/services/projects";
import { roleImageUrl } from "../../utils/roleAssets";
import {
  CARD_HEIGHT_PX,
  CARD_WIDTH_PX,
  CardScaleCanvas,
  cardScaleFor,
} from "./OfferUserCard";
import type { OfferWheelLayout } from "./useOfferWheelLayout";
import { useDeferredReclick } from "./useDeferredReclick";

interface RolePickerProps {
  /** 選択中のロール ID */
  value?: number;
  /** ロール選択時のコールバック（サマリ表示用に役割名も併せて返す） */
  // eslint-disable-next-line no-unused-vars
  onChange: (roleId: number, info: { roleNameJp: string }) => void;
  /**
   * arc モードで Stage 余白がクリックされたときの「無選択へ戻す」コールバック。
   * 親（OfferWizardOverlay）が `setRoleId(undefined)` をディスパッチする想定。
   */
  onDeselect?: () => void;
  /** Shift ホールドのスタックプレビュー開始 / 解除通知（副パネルを隠す用途）。 */
  // eslint-disable-next-line no-unused-vars
  onStackChange?: (active: boolean) => void;
  /** カードのダブルクリック / Enter で「次へ」相当の決定を行う。 */
  onConfirmSelection?: () => void;
  /**
   * arc モードのカード寸法 / 縦中心。空いている領域へフィットさせた値を
   * `OfferWizardOverlay` が算出して渡す。`null` のときは基準寸法で描く。
   */
  wheelLayout?: OfferWheelLayout | null;
  /**
   * 指定すると、そのユーザーがアクティブにしているロールのみに絞り込む。
   * オファー作成ウィザードで「①送信先（フォロー中ユーザー）→ ②ロール」の
   * 並び順のときに使用する。
   */
  filterByUserId?: number;
  /**
   * 並び方の表示モード。
   *  - "list"（既定）: 既存の縦並び RadioCard リスト
   *  - "arc": 円形/扇形 → ホイール回転のスクロール演出（RadioCardGroup）
   */
  variant?: "list" | "arc";
  /**
   * arc モードで「同じカードを再クリック」したときの表示切替フラグ。
   * `false`: `role_name_*` 系（既定）／`true`: `credit_title_*` 系。
   * カード右辺の縦書きラベルと、親側プレビューの両方に同じ状態を反映するため、
   * 親（OfferWizardOverlay 等）に state をリフトする想定。
   */
  showCreditTitle?: boolean;
  /** `showCreditTitle` 更新コールバック（親で state を保持する）。 */
  // eslint-disable-next-line no-unused-vars
  onShowCreditTitleChange?: (next: boolean) => void;
}

// マイページ通知 / 公開範囲設定と同じ「縦並び RadioCard」レイアウト。
const PickerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

/**
 * arc モード時に FilterChipBar を OverlayBody 上端に sticky させるためのラッパ。
 * z-index は RadioCardGroup の Stage(breakOut, z=2) より上の値にして、
 * 円弧アニメーションのカードが FilterChipBar を覆わないようにする。
 * 背景は持たせない（要望: 「半透明背景は削除」）ので、上端に張り付いたタグだけが
 * カードの上に見える形になる。
 */
const StickyTagBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 3;
  /* OverlayBody の左右 padding が 0 なので、自分でわずかな padding を確保。 */
  padding: 4px 0 8px;
`;

const RoleList = styled.div.attrs({ className: "u-col u-w100" })`
  gap: 16px;
`;

const RoleCardLabel = styled.div.attrs({ className: "u-col u-gap4 u-w100" })``;

const RoleCardTitleRow = styled.div.attrs({
  className: "u-row u-gap8 u-align-center",
})`
  flex-wrap: wrap;
`;

const RoleCardTitle = styled.label.attrs({ className: "heading-13" })``;

const RoleCardDescription = styled.p.attrs({ className: "bodytext-13" })`
  color: var(--soremo-text);
`;

const EmptyState = styled.div.attrs({ className: "bodytext-13" })`
  padding: 24px 0;
  text-align: center;
  color: var(--soremo-light-gray);
`;

// ===== Portrait レイアウト（arc モード専用） =====
// 1:1.414 縦長アスペクトの「サムネイル全面 + 左下アイコン + 右辺縦書きクレジット名」のマガジン風。
// 既存の縦リストは横長 RadioCard なので、こちらは arc モードでのみ使う。
// 説明テキストは選択後の別 UI で表示するため、ここでは出さない（要件: クリックで表示）。

const PortraitCard = styled.div<{ $checked: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 12px;
  border: 1px solid
    ${({ $checked }) =>
      $checked ? "var(--soremo-blue)" : "var(--soremo-border)"};
  outline: 1px solid
    ${({ $checked }) =>
      $checked ? "var(--soremo-blue)" : "var(--soremo-border)"};
  background: linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%);
  overflow: hidden;
  transition: border-color 0.2s, outline-color 0.2s;
`;

const PortraitThumbnail = styled.div<{ $imageUrl: string }>`
  position: absolute;
  inset: 0;
  background-image: url(${({ $imageUrl }) => $imageUrl});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: var(--soremo-light-gray);
`;

/**
 * カード左下に配置するポジションアイコン（Material Symbols Rounded）。
 *
 * `mix-blend-mode: difference` + 白で塗ることで、サムネ画像のどんな色味に対しても
 * 反転コントラストで描画される。`opacity` で反転の強度を弱め、全体的に明るめに
 * 落ち着かせる。アイコン種別の判定は `roleStatusIcon()` で行う。
 *
 * `$compact=true` のときはサイズを 24px に縮小する（`hub` アイコンが視覚的に
 * `workspace_premium` / `license` より大きく見えるため、見た目バランス調整用）。
 */
const PositionIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})<{ $compact?: boolean }>`
  position: absolute;
  left: 12px;
  bottom: 12px;
  font-size: ${({ $compact }) => ($compact ? "24px" : "32px")};
  color: #ffffff;
  mix-blend-mode: difference;
  opacity: 0.7;
  pointer-events: none;
  font-variation-settings: ${({ $compact }) =>
    $compact
      ? `"FILL" 0, "wght" 500, "GRAD" 0, "opsz" 24`
      : `"FILL" 0, "wght" 500, "GRAD" 0, "opsz" 32`};
`;

/**
 * カード右辺に縦書きで描くクレジット名（英語）。マガジン風タイポグラフィ。
 *
 * `writing-mode: vertical-rl` で Latin 文字が 90° CW に回転して上→下へ流れる。
 * フォントは Typekit にグローバル登録済の DIN 2014 ExtraBold（`code-font3` 等で使用中）。
 * `mix-blend-mode: difference` + `opacity` でサムネ画像との視認性を確保しつつ
 * 反転コントラストを弱め、明るめに見せる。
 *
 * 縦方向はカード上下 12px までフルに使う。左下のアイコンとは横位置が違うので
 * カード高さ全域まで伸ばしても衝突しない。
 */
const RotatedCreditTitle = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  bottom: 12px;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  font-family: "din-2014", sans-serif;
  font-weight: 800;
  font-style: normal;
  font-size: 24px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #ffffff;
  mix-blend-mode: difference;
  opacity: 0.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
`;

const ADMIN_USER_ROLES = new Set(["MASTERADMIN", "CURATOR"]);

/**
 * RoleTag → FilterChipBar に渡す ShotTag 形へのアダプタ。
 * id を string 化し、ShotTag 側で要求される `sort_rule` / `position` を補う。
 */
const toShotTagShape = (tag: RoleTag): ShotTag => ({
  id: String(tag.id),
  name: tag.name,
  sort_rule: "newest",
  position: tag.order,
});

const RolePicker: React.FC<RolePickerProps> = ({
  value,
  onChange,
  onDeselect,
  onStackChange,
  onConfirmSelection,
  wheelLayout,
  filterByUserId,
  variant = "list",
  showCreditTitle = false,
  onShowCreditTitleChange,
}) => {
  const { data: tagsData } = useGetRoleTagsQuery();
  const [selectedTagIdStr, setSelectedTagIdStr] = useState<string | null>(null);
  /**
   * arc モードで「タグ切替時に scroller を先頭まで戻す」ために使う参照。
   * 最寄りの `[data-offer-overlay-scroll]` 要素を closest で解決する（RadioCardGroup
   * 内部のスクローラ解決ロジックと同じ）。
   */
  const wrapperRef = useRef<HTMLDivElement>(null);
  /**
   * タグ切替操作直後に items（roles）が確定したタイミングで「もう一度トップに戻す」
   * 保険を発火するためのペンディングフラグ。imperative の `scrollTo` だけだと、
   * useLayoutEffect / RadioCardGroup の内部 updateFromScroll と timing 競合して
   * アニメが途中で打ち消されるケースがあるため、items 更新を待ってから再度スクロール
   * リセットを試みる useEffect とのコンビで信頼性を上げる。
   */
  const pendingTagResetRef = useRef<boolean>(false);
  // 再クリックの表記トグルは、ダブルクリック（=「次へ」）の 1 打目で暴発しないよう
  // 少し遅延させて実行する。
  const reclick = useDeferredReclick();

  const selectedTagId = useMemo(
    () => (selectedTagIdStr === null ? undefined : Number.parseInt(selectedTagIdStr, 10)),
    [selectedTagIdStr],
  );

  const { data: rolesData, isFetching: isFetchingRoles } = useGetRolesQuery({
    tag_id: selectedTagId,
    limit: 100,
  });

  // 送信先ユーザーが先に決まっているとき、そのユーザーのアクティブロールに絞り込む。
  const {
    data: userRolesData,
    isFetching: isFetchingUserRoles,
    isError: isUserRolesError,
  } = useGetUserRolesQuery(filterByUserId as number, {
    skip: filterByUserId === undefined,
  });

  const allowedRoleIdSet = useMemo<Set<number> | null>(() => {
    if (filterByUserId === undefined) return null;
    const list = userRolesData?.data?.roles;
    if (!list) return new Set<number>();
    return new Set(list.map((r) => r.id));
  }, [filterByUserId, userRolesData]);

  const { data: currentUser } = useGetCurrentUserQuery();
  const userRole: string | undefined = currentUser?.data?.role;
  const isAdmin = !!userRole && ADMIN_USER_ROLES.has(userRole);

  const [reorderRoleTags] = useReorderRoleTagsMutation();

  // タグの並び順は API 側 `order` 昇順想定。Admin が並び替えたら永続化を試みる。
  const [localOrder, setLocalOrder] = useState<RoleTag[] | null>(null);
  const tagsForBar: ShotTag[] = useMemo(() => {
    const source = localOrder ?? tagsData?.data?.tags ?? [];
    return source.map(toShotTagShape);
  }, [localOrder, tagsData]);

  /**
   * 最寄りの `[data-offer-overlay-scroll]` 要素を取得し、先頭へ smooth scroll する。
   * scroller が見つからない / すでに `scrollTop=0` の場合は no-op になる
   * （items 入れ替え側で初期円形配置に自動再描画される前提のため）。
   */
  const triggerScrollResetToTop = useCallback((): void => {
    const wrapperEl = wrapperRef.current;
    if (!wrapperEl) return;
    const scrollerEl = wrapperEl.closest("[data-offer-overlay-scroll]");
    if (!(scrollerEl instanceof HTMLElement)) return;
    if (scrollerEl.scrollTop <= 0) return;
    scrollerEl.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleSelectTag = useCallback(
    (tagId: string | null) => {
      setSelectedTagIdStr(tagId);
      // arc モードのとき、ロールタグを切り替えると items が入れ替わるので、
      // 最寄りの scroller を 0 に戻して RadioCardGroup を Phase 0（初期円形配置）
      // から描き直させる。`behavior: "smooth"` でホイールが解けて円形に戻る演出を
      // 見せつつ、scroll event は RadioCardGroup の listener に拾われて
      // progress / fractionalCenter を 0 にリセットしてくれる。
      //
      // imperative 単発だと、`setSelectedTagIdStr` 直後の React commit や
      // RadioCardGroup の useLayoutEffect 経由の updateFromScroll と timing 競合して
      // アニメが途中で打ち消されることがある。フラグを立てて items 確定後の
      // useEffect でも再度トップに戻すことで信頼性を上げる。
      if (variant !== "arc") return;
      pendingTagResetRef.current = true;
      requestAnimationFrame(() => {
        triggerScrollResetToTop();
      });
    },
    [variant, triggerScrollResetToTop],
  );

  const handleReorder = useCallback(
    (reorderedShotTags: ShotTag[]) => {
      const idOrder = reorderedShotTags.map((t) => Number.parseInt(t.id, 10));
      const sourceTags = tagsData?.data?.tags ?? [];
      const byId = new Map(sourceTags.map((t) => [t.id, t]));
      const next = idOrder
        .map((id) => byId.get(id))
        .filter((t): t is RoleTag => !!t);
      setLocalOrder(next);
      // 失敗しても UI 上の並びは保つ（再フェッチ時に正解で上書きされる）
      void reorderRoleTags({ ordered_ids: idOrder }).catch(() => {
        // 失敗時のリカバリは将来のロール管理画面に委ねる
      });
    },
    [tagsData, reorderRoleTags],
  );

  const roles: RoleListItem[] = useMemo(() => {
    const all = rolesData?.data?.roles ?? [];
    if (!allowedRoleIdSet) return all;
    return all.filter((r) => allowedRoleIdSet.has(r.id));
  }, [rolesData, allowedRoleIdSet]);

  // タグ切替直後 → items（roles）が新規取得で更新されたタイミングで、
  // imperative の `scrollTo` が timing で消えていたとしてももう一度トップに戻す。
  // `pendingTagResetRef` はタグ押下時にだけ立つので、初回マウントや
  // タグと無関係の roles 更新（例: 同タグの再フェッチ）では発火しない。
  useEffect(() => {
    if (!pendingTagResetRef.current) return;
    if (variant !== "arc") return;
    if (roles.length === 0) return; // ローディング中は何もしない
    pendingTagResetRef.current = false;
    // RadioCardGroup の useLayoutEffect が回って scroll listener / 内部状態が
    // 更新された後で再スクロールを試みるため、次フレームに回す。
    requestAnimationFrame(() => {
      triggerScrollResetToTop();
    });
  }, [roles, variant, triggerScrollResetToTop]);

  const isFetching =
    isFetchingRoles || (filterByUserId !== undefined && isFetchingUserRoles);

  const emptyMessage = isUserRolesError
    ? "対象ユーザーのロールが取得できませんでした"
    : "該当するロールがありません";

  // arc モードのカード中身は基準寸法（256×362 = ポップオーバーのカード）で
  // 描き、実寸へ等比スケールする（縦書きクレジット名 / アイコン / 余白の
  // バランス維持）。送信先ステップ（RecipientStep）と同じ流儀。
  const arcCardScale = cardScaleFor(
    wheelLayout?.cardSize.width ?? CARD_WIDTH_PX,
  );

  /**
   * 縦リスト（"list" variant）用の RadioCard レンダラ。
   * arc モードでは見た目専用の `PortraitCard` を inline 描画し、クリック / a11y / 位置決めは
   * 外側の `RadioCardGroup` の Slot に任せるため、こちらは使わない。
   */
  const renderListRoleCard = (
    role: RoleListItem,
    opts: { checked: boolean; onClick: () => void },
  ) => {
    const label = roleStatusLabel(
      role.accept_position,
      role.allow_subcontracting,
    );
    return (
      <RadioCard
        name="role"
        id={role.id}
        width="128px"
        height="128px"
        showImage
        showRadio
        checked={opts.checked}
        onClick={opts.onClick}
      >
        <RoleCardLabel>
          <RoleCardTitleRow>
            <RoleCardTitle>{role.role_name_jp}</RoleCardTitle>
            {label ? (
              <StatusBadge
                variant="inline"
                label={label}
                highlighted
              />
            ) : null}
          </RoleCardTitleRow>
          {role.description && (
            <RoleCardDescription>{role.description}</RoleCardDescription>
          )}
        </RoleCardLabel>
      </RadioCard>
    );
  };

  return (
    <PickerWrapper ref={wrapperRef}>
      {/*
       * arc モードでは Wrapper 高さが巨大（=stageHeight + 数千 px の sticky 区間）
       * なので、FilterChipBar が単純に flow に置かれると下方向にスクロール途中で
       * 見えなくなる。`position: sticky` で OverlayBody 上端に張り付ける。
       * list モードでは Wrapper が短く sticky の効果は出ないが副作用は無い。
       */}
      <StickyTagBar>
        <FilterChipBar
          items={tagsForBar}
          selectedId={selectedTagIdStr}
          onSelect={handleSelectTag}
          onReorder={isAdmin ? handleReorder : undefined}
          align="center"
        />
      </StickyTagBar>

      {isFetching && roles.length === 0 ? (
        <EmptyState>ロールを読み込み中...</EmptyState>
      ) : roles.length === 0 ? (
        <EmptyState>{emptyMessage}</EmptyState>
      ) : variant === "arc" ? (
        <RadioCardGroup<RoleListItem>
          items={roles}
          value={value}
          onChange={(roleId, role) => {
            // 同じカードを再クリックした場合だけ、英語/日本語ラベル種別をトグル
            // （カード右辺縦書き + 親側プレビューの両方に反映）。
            // 別カードへの遷移時は既定（role_name_*）に戻し、親へ通知する。
            if (value === roleId) {
              // ダブルクリックの 1 打目で表記が裏返らないよう、トグルは遅延実行する
              // （猶予内に onConfirm が来たら取り消される）。
              reclick.schedule(() =>
                onShowCreditTitleChange?.(!showCreditTitle),
              );
              return;
            }
            reclick.cancel();
            onShowCreditTitleChange?.(false);
            onChange(role.id, { roleNameJp: role.role_name_jp });
          }}
          // ダブルクリック / Enter の「決定」で「次へ」。ホーム・プロジェクトの
          // ホイールと操作感を揃える（あちらは決定で該当ページへ遷移する）。
          onConfirm={() => {
            reclick.cancel();
            onConfirmSelection?.();
          }}
          onDeselect={() => {
            reclick.cancel();
            onShowCreditTitleChange?.(false);
            onDeselect?.();
          }}
          // カード寸法・縦中心は空いている領域へフィットさせた値を親から受ける。
          // 送信先（ユーザーカード）と同じ値なので、両ステップで寸法が揃う
          // （未算出のフレームだけ共通の基準寸法にフォールバック）。
          cardSize={
            wheelLayout?.cardSize ?? {
              width: CARD_WIDTH_PX,
              height: CARD_HEIGHT_PX,
            }
          }
          verticalCenterY={wheelLayout?.verticalCenterY}
          // RightColumn の max-width を超えて利用可能な右領域いっぱいまで広げる。
          breakOut
          // カード列を右カラムの左右端までフルに見せる（クリップ余白なし）。
          breakOutInsetPx={0}
          // 扇の段階でもカードを等倍で見せる（ホーム / プロジェクトと同じ）。
          scaleFan={1}
          // ジェスチャ挙動を他のホイール（ホーム / プロジェクト / 送信先）と
          // 統一する。内部スクロールを持つカードが無いため操作は素通しになる。
          cardScrollHandoff
          // Stage にフォーカスが無くても ← / → でカードを送れるようにする
          // （ホーム / プロジェクトのホイールと同じ操作感）。
          globalKeyboardNav
          // Shift ホールドで全カードを手札状の扇に集約して全体量を見渡す。
          stackPreview={{ count: roles.length }}
          onStackChange={onStackChange}
          ariaLabel="ロールを選択"
          renderCard={(role, state) => {
            const statusLabel = roleStatusLabel(
              role.accept_position,
              role.allow_subcontracting,
            );
            const iconName = roleStatusIcon(
              role.accept_position,
              role.allow_subcontracting,
            );
            const rotatedLabel = pickRoleDisplayName(
              role.role_name_en,
              role.credit_title_en,
              state.checked && showCreditTitle,
            );
            return (
              <CardScaleCanvas $scale={arcCardScale}>
                <PortraitCard $checked={state.checked}>
                  <PortraitThumbnail $imageUrl={roleImageUrl(role.id)} />
                  {rotatedLabel ? (
                    <RotatedCreditTitle aria-hidden="true">
                      {rotatedLabel}
                    </RotatedCreditTitle>
                  ) : null}
                  {iconName ? (
                    <PositionIcon
                      role="img"
                      aria-label={statusLabel ?? undefined}
                      $compact={iconName === "hub"}
                    >
                      {iconName}
                    </PositionIcon>
                  ) : null}
                </PortraitCard>
              </CardScaleCanvas>
            );
          }}
        />
      ) : (
        <RoleList>
          {roles.map((role) => (
            <React.Fragment key={role.id}>
              {renderListRoleCard(role, {
                checked: value === role.id,
                onClick: () =>
                  onChange(role.id, { roleNameJp: role.role_name_jp }),
              })}
            </React.Fragment>
          ))}
        </RoleList>
      )}
    </PickerWrapper>
  );
};

export default RolePicker;
